import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dices, Loader2 } from 'lucide-react';

import type { RankedQueue, RankedQueueNotice } from '@/hooks/useRankedQueue';

/**
 * i18n key + interpolation for a notice.
 *
 * Deliberately NOT an exhaustive switch on the cancel reason: the gameserver's
 * reason list grows and this bundle keeps running long after it was compiled,
 * so an unknown reason must fall through to generic copy — never throw, never
 * print the raw protocol token at a player. `useRankedQueue` already narrows
 * anything the shared types package doesn't recognise to `'unknown'`.
 */
function noticeCopy(notice: RankedQueueNotice): {
  key: string;
  params?: Record<string, unknown>;
} {
  if (notice.kind === 'failed') return { key: 'ranked.joinFailed' };
  if (notice.kind === 'atMatchCap') {
    return notice.max != null
      ? { key: 'ranked.atMatchCapN', params: { max: notice.max } }
      : { key: 'ranked.atMatchCap' };
  }
  switch (notice.reason) {
    case 'capped':
      return { key: 'ranked.cancelledCapped' };
    case 'timeout':
      return { key: 'ranked.cancelledTimeout' };
    case 'duplicate':
      return { key: 'ranked.cancelledDuplicate' };
    default:
      return { key: 'ranked.cancelledGeneric' };
  }
}

/** Pairing window widens from ±50 to ±200 ELO after this long (server-side). */
const WIDEN_AFTER_MS = 30_000;

/** mm:ss for the count-up timer. */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface RankedQueueSheetProps {
  queue: RankedQueue;
}

/**
 * Ranked-queue bottom sheet for backgammon. Mirrors the QDuels QueueSheet shell
 * (dim backdrop + rounded bottom sheet, brand spinner, count-up timer, Cancel)
 * but is fully self-contained — it only reads the `useRankedQueue` state the
 * parent owns, so the parent keeps control of navigation on pairing.
 *
 * Shown while `status` is `queueing` / `queued` / `paired`, AND while a
 * `notice` is pending — the server can stop the search on its own (it is at the
 * concurrent-match cap, the entry timed out, …) and dropping the player back to
 * the lobby with no explanation reads as a bug. Backdrop tap and the Cancel
 * button call `queue.cancel()`. After 30s it surfaces the "widening the search"
 * hint, matching the server's ±50 → ±200 ELO window expansion. On `paired` it
 * flashes "Match found! vs {name}" for a beat before the parent navigates.
 */
export function RankedQueueSheet({ queue }: RankedQueueSheetProps) {
  const { t } = useTranslation('backgammon');
  const { status, enqueuedAt, paired, notice, cancel, dismissNotice } = queue;

  const [elapsed, setElapsed] = useState(0);

  // Count-up clock while waiting. Anchors on the server-confirmed enqueue time
  // when available, otherwise on mount, and ticks once a second.
  useEffect(() => {
    if (status !== 'queueing' && status !== 'queued') {
      setElapsed(0);
      return;
    }
    const start = enqueuedAt ?? Date.now();
    setElapsed(Date.now() - start);
    const id = window.setInterval(() => setElapsed(Date.now() - start), 1_000);
    return () => window.clearInterval(id);
  }, [status, enqueuedAt]);

  const widened = elapsed >= WIDEN_AFTER_MS;
  const isPaired = status === 'paired' && paired;
  // A pending notice takes over the sheet: the search is already stopped, so
  // the spinner would be a lie. Dismissing it (button or backdrop) closes the
  // sheet — `cancel()` would also fire a pointless DELETE for a queue entry the
  // server has already dropped.
  const stopped = notice ? noticeCopy(notice) : null;
  const dismiss = stopped ? dismissNotice : cancel;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={stopped ? t('ranked.close') : t('ranked.cancel')}
        onClick={dismiss}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Dices className="h-4 w-4 text-brand" />
            {stopped ? t('ranked.stopped') : t('ranked.title')}
          </h2>
        </div>

        {stopped ? (
          /* ── the server stopped the search: say why, then get out of the way ── */
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-center text-sm font-semibold text-fg">
              {t(stopped.key, stopped.params)}
            </p>
            <button
              type="button"
              onClick={dismissNotice}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('ranked.close')}
            </button>
          </div>
        ) : isPaired ? (
          /* ── paired: brief confirmation before the parent navigates ─────── */
          <div className="flex flex-col items-center gap-4 py-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Dices className="h-6 w-6" />
            </span>
            <p className="text-center text-base font-bold text-fg">
              {t('ranked.found', { name: paired.opponent.username })}
              <span className="ml-1 font-semibold tabular-nums text-brand">
                ({paired.opponent.elo})
              </span>
            </p>
          </div>
        ) : (
          /* ── queueing / queued: spinner + elapsed + Cancel ─────────────── */
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-10 w-10 animate-spin text-brand" />
            <div className="text-center">
              <p className="text-sm font-semibold text-fg">
                {widened ? t('ranked.searchWide') : t('ranked.searching')}
              </p>
              <p className="mt-1 text-xs tabular-nums text-fg-muted">
                {formatElapsed(elapsed)}
              </p>
            </div>

            <button
              type="button"
              onClick={cancel}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('ranked.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
